import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { answerText, questionChoices, questionLabels } from '@/features/surveys/answerFormat';
import {
  fetchAnswerFileUrl,
  fetchSurveyResponses,
  type FileAnswerValue,
  type Question,
} from '@/features/surveys/api';
import { ApiError } from '@/lib/api/http';

const STATUS_LABELS: Record<string, string> = {
  draft: '下書き',
  submitted: '提出済み',
  resubmitted: '再提出済み',
};

function isFileAnswerValue(value: unknown): value is FileAnswerValue {
  return typeof value === 'object' && value !== null && 'key' in value && 'filename' in value;
}

/**
 * 個別の回答一覧（事務局・サクセス / DECISIONS D-018）. 匿名方式が full の場合はAPI側で氏名が伏せられる.
 * 設問文・選択肢は `questions`（版の設問定義）から引いて日本語で出す（D-083・D-158）。
 */
export function SurveyResponsesList({
  eventSurveyId,
  eventId,
  questions,
}: {
  eventSurveyId: string;
  /** 氏名リンク先（この回の結果画面 / D-341） */
  eventId?: string;
  questions: Question[];
}): ReactElement {
  const query = useQuery({
    queryKey: ['survey-responses', eventSurveyId],
    queryFn: () => fetchSurveyResponses(eventSurveyId),
  });

  if (query.isPending) return <p className="text-wptext-muted">読み込み中…</p>;
  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 403) {
      return <p className="text-wptext-muted">回答一覧を閲覧する権限がありません。</p>;
    }
    return <p className="text-wpnotice-error">読み込みに失敗しました。</p>;
  }

  const responses = query.data;
  const labels = questionLabels(questions);
  const choices = questionChoices(questions);
  if (responses.length === 0) return <p className="text-wptext-muted">まだ回答がありません。</p>;

  async function openFile(memberId: string, questionId: string): Promise<void> {
    const { url } = await fetchAnswerFileUrl(eventSurveyId, memberId, questionId);
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  return (
    <div className="flex flex-col gap-3">
      {responses.map((response) => (
        <div key={response.id} className="rounded-sm border border-wpborder bg-white p-3">
          <p className="mb-2 font-semibold text-wptext-strong">
            {response.member_id && eventId ? (
              <Link
                to={`/members/${response.member_id}/events/${eventId}`}
                className="text-wpaccent hover:underline"
              >
                {response.member_name ?? response.member_id}
              </Link>
            ) : (
              (response.member_name ??
                response.respondent_name ??
                response.member_id ??
                response.respondent_user_id ??
                '匿名')
            )}{' '}
            — {STATUS_LABELS[response.status]}
            {response.is_overdue && <span className="ml-2 text-wpnotice-error">期限超過</span>}
          </p>
          <ul className="flex flex-col gap-1 text-wptext-muted">
            {response.answers.map((a) => (
              <li key={a.question_id}>
                <span className="text-wptext-strong">{labels[a.question_id] ?? a.question_id}</span>
                {'：'}
                {isFileAnswerValue(a.value) ? (
                  response.member_id ? (
                    <button
                      type="button"
                      onClick={() => void openFile(response.member_id as string, a.question_id)}
                      className="text-wpaccent hover:underline"
                    >
                      {a.value.filename}
                    </button>
                  ) : (
                    a.value.filename
                  )
                ) : (
                  answerText(a.value, choices[a.question_id])
                )}
              </li>
            ))}
            {response.answers.length === 0 && <li>回答なし</li>}
          </ul>
        </div>
      ))}
    </div>
  );
}
